import classnames from 'classnames';
import React, { useState } from 'react';

import { certifyUserTicket } from 'common/api/CertifyUserTicket';
import Button from 'common/components/Button';
import allocineContainer from 'common/containers/allocineContainer';
import { ModalNames } from 'common/module/ui/lazy-modal/constant-types';
import buildReactDomModalModule from 'common/module/ui/lazy-modal/react-dom-modal-module';
import ReactModal from 'common/module/ui/lazy-modal/react-modal';
import { CloseModal } from 'common/module/ui/lazy-modal/types';
import { path } from 'common/tools/network/routing';
import trans from 'common/tools/translations/trans';
import socialActionTracking from 'common/tracking/socialActionTracking';

import styles from './styles.module.scss';

const reviewVerifiedDisclaimer = trans('review.verified.disclaimer', {
  cssBold: styles.boldParagraph,
  url: path('public_review', {}, true)
});

export type VerifyRatingModalParams = {
  entityId?: string;
  entityTitle?: string;
  onVerify?: () => void;
  onVerified?: () => void;
  fromUserRating?: boolean;
  entity?: {
    internalId?: number | null;
    title?: string | null;
    typename?: string;
  };
};

type VerifyRatingModalProps = {
  closeModal: CloseModal;
  entityId?: string;
  entityTitle?: string;
  onVerify?: () => void;
  onVerified?: () => void;
  fromUserRating?: boolean;
  entity?: {
    internalId?: number | null;
    title?: string | null;
    typename?: string;
  };
};

const VerifyRatingModal = ({
  closeModal,
  entityId,
  entityTitle,
  onVerify,
  onVerified,
  fromUserRating = false,
  entity
}: VerifyRatingModalProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    if (!entityId) {
      return;
    }

    setIsVerifying(true);
    setError(null);

    if (entity && entity.typename) {
      socialActionTracking('rate_verify', 'add', {
        id: entityId,
        internalId: entity.internalId ?? undefined,
        title: entity.title ?? entityTitle ?? undefined,
        typename: entity.typename
      });
    }

    if (onVerify) {
      onVerify();
    }

    try {
      await certifyUserTicket(entityId);
      setIsSuccess(true);
      setIsVerifying(false);

      closeModal();
      if (onVerified) {
        window.setTimeout(() => {
          onVerified();
        }, 100);
      }
    } catch (error) {
      setIsVerifying(false);
      setError(trans('review.verified.error'));
    }
  };

  const handleClose = () => {
    closeModal();
  };

  const getConfirmationContent = () => {
    if (error) {
      return <span>{error}</span>;
    }

    if (fromUserRating) {
      return (
        <div dangerouslySetInnerHTML={{ __html: reviewVerifiedDisclaimer }} />
      );
    }

    return (
      <>
        {trans('review.confirmation-message.main')}.
        <br />
        <br />
        <div dangerouslySetInnerHTML={{ __html: reviewVerifiedDisclaimer }} />
      </>
    );
  };

  const confirmationText = getConfirmationContent();

  return (
    <ReactModal size="large" className={styles.modalReview}>
      <div className={styles.confirmationContainer}>
        <h1 className={styles.confirmationTitle}>
          {trans('review.verified.credibility.boost')}
        </h1>
        <p className={styles.confirmationText}>{confirmationText}</p>
        <div className={styles.confirmationButtonsContainer}>
          <Button
            size="md"
            className="button-verified-rating"
            onClick={handleVerify}
            disabled={isVerifying || isSuccess}
          >
            <i
              className={classnames('icon', {
                [styles.iconSpinner]: isVerifying || isSuccess,
                'icon-tick': !isVerifying && !isSuccess
              })}
            />
            {trans('review.verified.label', {
              isVerifying: isVerifying || isSuccess ? 1 : 0
            })}
          </Button>
          {!fromUserRating && (
            <Button
              size="md"
              theme="inverse"
              className="button-verify-close"
              onClick={handleClose}
              disabled={isVerifying}
            >
              {trans('review-editor.dismiss')}
            </Button>
          )}
        </div>
      </div>
    </ReactModal>
  );
};

const WrappedVerifyRatingModal = allocineContainer(VerifyRatingModal);

export default buildReactDomModalModule<VerifyRatingModalParams>(
  ModalNames.VERIFY_RATING_MODAL,
  (params = {}, closeModal) => {
    return <WrappedVerifyRatingModal closeModal={closeModal} {...params} />;
  }
);
